// Currently using this expenses PER CARD STYLE

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "axios";
import { Form } from "react-bootstrap";
import swal from "sweetalert";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./NewAddedstyleExpenses.css";
import BASE_URL from "../../../assets/global/url";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import { addDays } from "date-fns";
import Select from "react-select";
import { selectCustomStyles } from "../../../assets/global/selectCustomStyles";
import useDarkMode from "../../../components/Themes/Usedarkmode";
import CustomDatePicker from "../../../components/CustomDatePicker";
import SharedTypedDateInput from "../../../components/inputs/TypedDateInput";
import useFormattedNumberInput from "../../../hooks/customHook/Number_Formatter";
import { checkCutoffDate } from "../../../utils/cutoffDateValidation";

// Cutoff warning for a selected date (posted cutoff / no cutoff yet). Only
// warns, the date is kept. Called from the date change handlers below, which
// are also CustomDatePicker's `setter`, so its "Today" button is covered too.
// The date inputs (TypedDateInput) only send complete dates.
const warnCutoffDate = (date, dateLabel) => {
  if (date) checkCutoffDate(date, dateLabel);
};

const dateToday = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const year = today.getFullYear();

  return `${year}-${month}-${day}`;
};

// Compares two dates by calendar day only (ignores time-of-day), so a
// plain "YYYY-MM-DD" string and a Date object produced by the picker
// still compare correctly even when their internal times differ.
const toDateOnly = (value) => {
  const d = new Date(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// Max characters allowed in an expense card's Description. Also drives the
// "0 / 500" counter under the textarea.
const DESC_MAX_LENGTH = 500;

// Symbol shown in front of the Amount input, taken from the selected
// currency's name (e.g. PHP -> peso sign, USD -> dollar sign). Returns null
// when the name isn't a valid ISO currency code, so the caller can show a
// generic icon instead of a wrong symbol.
const getCurrencySymbol = (code) => {
  if (!code) return null;
  try {
    const part = new Intl.NumberFormat("en", {
      style: "currency",
      currency: String(code).trim().toUpperCase(),
      currencyDisplay: "narrowSymbol",
    })
      .formatToParts(0)
      .find((p) => p.type === "currency");
    return part ? part.value : null;
  } catch {
    return null;
  }
};

// Custom input for DatePicker: typed as MM/dd/yyyy and shown as Oct/10/2026
// (components/inputs/TypedDateInput), or picked from the calendar. Only a
// complete date reaches the change handlers, so the cutoff warning doesn't run
// on a half-typed date. Adds the year list reset on click.
// Shared across every expense entry, defined once at module scope.
const TypedDateInput = React.forwardRef(
  (
    {
      value,
      onClick,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      disabled,
      generateYears,
      className,
    },
    ref,
  ) => {
    return (
      <SharedTypedDateInput
        className={className}
        onClick={(e) => {
          if (onClick) onClick(e);

          let selectedYear = new Date().getFullYear();
          if (value) {
            const parsed = new Date(value);
            // Extract year safely if value is a valid date
            if (!isNaN(parsed.getTime())) {
              selectedYear = parsed.getFullYear();
            }
          }

          // Reset/Initialize Year List based on selected date
          if (generateYears && typeof generateYears === "function") {
            generateYears(selectedYear);
          }
        }}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        value={value}
        ref={ref}
        disabled={disabled}
        required
      />
    );
  },
);

const CustomInput = React.forwardRef((props, ref) => (
  <TypedDateInput {...props} className="form-control p-2 w-100" ref={ref} />
));

// Smaller date-picker input used inside each expense card (vs the full-size
// CustomInput used in General Fields above the grid). Same height as before
// (form-control-sm) so CustomDatePicker's iconTopOffset still lines up; the
// wrapper only adds the calendar icon on the left, like the card's other fields.
const CompactCustomInput = React.forwardRef((props, ref) => (
  <div className="ee-icon-field">
    <TypedDateInput
      {...props}
      className="form-control form-control-sm w-100 ee-has-icon"
      ref={ref}
    />
    <i className="fa-regular fa-calendar ee-field-icon" aria-hidden="true"></i>
  </div>
));

// Wraps selectCustomStyles(...) so the Accountable Person / Expenses Type 1
// react-select boxes render at the same height as the plain
// "form-control p-2" inputs next to them (e.g. Transaction ID).
// react-select's own Control has a built-in min-height plus its own
// ValueContainer/indicator padding, and those keep stacking on top of
// whatever padding value gets passed into selectCustomStyles - so the box
// still ends up taller even when that padding value looks small. This caps
// the height directly instead of relying on padding alone.
// It also rounds the control to match the card's other inputs and leaves
// room on the left for the icon each card overlays on top of it.
const compactSelectStyles = (...args) => {
  const base = selectCustomStyles(...args);
  return {
    ...base,
    control: (provided, state) => ({
      ...(base.control ? base.control(provided, state) : provided),
      // "height" (not just minHeight) in case selectCustomStyles sets an
      // explicit fixed height - an explicit height wins over minHeight, so
      // this resets it back to content-driven before minHeight below floors it.
      height: "auto",
      minHeight: "calc(1.3em + 0.5rem + 2px)", // ~form-control-sm height
      borderRadius: "8px",
    }),
    valueContainer: (provided, state) => ({
      ...(base.valueContainer
        ? base.valueContainer(provided, state)
        : provided),
      // 2.1rem on the left = same text offset as the card's icon inputs.
      padding: "0px 6px 0px 2.1rem",
    }),
    indicatorsContainer: (provided, state) => ({
      ...(base.indicatorsContainer
        ? base.indicatorsContainer(provided, state)
        : provided),
      padding: "0 2px",
    }),
    // The dropdown arrow / clear "x" are flex children inside
    // indicatorsContainer - their OWN default padding (react-select ships
    // 8px) pushes that row's natural height regardless of the container's
    // padding above, so they need shrinking directly too.
    dropdownIndicator: (provided, state) => ({
      ...(base.dropdownIndicator
        ? base.dropdownIndicator(provided, state)
        : provided),
      padding: "2px",
    }),
    clearIndicator: (provided, state) => ({
      ...(base.clearIndicator
        ? base.clearIndicator(provided, state)
        : provided),
      padding: "2px",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (provided, state) => ({
      ...(base.menu ? base.menu(provided, state) : provided),
      borderRadius: "8px",
    }),
    input: (provided, state) => ({
      ...(base.input ? base.input(provided, state) : provided),
      margin: 0,
      padding: 0,
    }),
  };
};

// One expense entry, rendered as a single vertical card. Each card manages
// its own local state/hooks (Rules of Hooks safe, since each is its own
// component instance) and exposes its current values to the parent through
// a ref via getPayload().
const ExpenseEntryFields = forwardRef(function ExpenseEntryFields(
  {
    entryKey,
    index,
    canRemove,
    onRemove,
    currency_db,
    expensesType_db,
    userOptions,
    expenseType1Options,
    validated,
    fetchNewCode,
    clientCodeDisplay, // display-only EXP-YY-MM-0001 preview; real one is assigned on save
    generalExpensesDate,
    generalDueDate,
    generalForeign,
    onDateOverrideChange,
    forceSyncToken,
    prefill, // only populated for the first entry (Fixed Asset -> Expense flow)
  },
  ref,
) {
  const { themeSelectStyles } = useDarkMode();
  const hasFixedCurrency = !!(prefill?.currencyId && prefill.currencyId !== "null"); // prettier-ignore
  const hasFixedAmount = !!(prefill?.totalFixedAssetAmount && prefill.totalFixedAssetAmount !== "null"); // prettier-ignore

  const initialAmount = hasFixedAmount ? prefill.totalFixedAssetAmount : "";

  // Internal transaction_id (old format) - not shown, only sent on save.
  const [transaction_id, setTransaction_id] = useState("");
  const [expensesCheque, setExpensesCheque] = useState(""); // para sa yilujia kasi un ang pag babasihan nila
  const [totalAmountDisplay, handleTotalAmountChange] =
    useFormattedNumberInput(initialAmount);
  const [integer = "", decimal = ""] = totalAmountDisplay.split(".");

  const [desc, setDesc] = useState("");
  const [expenses_date, setExpenses_date] = useState("");
  const [dueDate, setDueDate] = useState("");
  // Once true, this card's own Expenses Date / Due Date no longer follow
  // the shared "general" date pair above the grid.
  const [dateOverridden, setDateOverridden] = useState(false);
  const [expensesSubType_id, setExpensesSubType_id] = useState();
  const [selected_currency_id, setSelected_currency_id] = useState(
    hasFixedCurrency
      ? prefill.currencyId
      : "11111111-1111-1111-1111-111111111111",
  );
  const [currencyRate, setCurrencyRate] = useState(1);
  const [accountable, setAccountable] = useState(null);
  const [isExternalAccountable, setIsExternalAccountable] = useState(false);
  const [externalAccountableText, setExternalAccountableText] = useState("");

  // Brief "copied" tick on the Transaction ID copy button.
  const [idCopied, setIdCopied] = useState(false);
  useEffect(() => {
    if (!idCopied) return;
    const t = setTimeout(() => setIdCopied(false), 1500);
    return () => clearTimeout(t);
  }, [idCopied]);

  // Get a fresh transaction code for this entry
  useEffect(() => {
    let isActive = true;

    fetchNewCode().then((code) => {
      if (!isActive) return;
      setTransaction_id(code);
    });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Follow the shared Expenses Date / Due Date until this card's own date
  // fields are edited directly. Runs on mount too, so a freshly added card
  // starts out synced to whatever the general date currently is.
  useEffect(() => {
    if (dateOverridden) return;
    setExpenses_date(generalExpensesDate);
    setDueDate(generalDueDate);
  }, [generalExpensesDate, generalDueDate, dateOverridden]);

  // Explicit "resync to general date" signal from the parent, fired when
  // the user answers the confirmation dialog with "Change All Cards". Unlike
  // the effect above, this re-syncs even a detached card and puts it back
  // in sync going forward. forceSyncToken only ever increments, so 0 safely
  // means "never triggered".
  useEffect(() => {
    if (!forceSyncToken) return;
    setDateOverridden(false);
    setExpenses_date(generalExpensesDate);
    setDueDate(generalDueDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceSyncToken]);

  // Fill in the currency rate once currencies load, for fixed-asset prefilled entries
  useEffect(() => {
    if (hasFixedCurrency && currency_db.length > 0) {
      const curr = currency_db.find(
        (data) => String(data.id) === String(prefill.currencyId),
      );
      if (curr) setCurrencyRate(curr.currency_rate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency_db]);

  const handleExpensesChange = (selectedOption) => {
    setExpensesSubType_id(selectedOption);
  };

  const handleChangeCurrency = (value) => {
    const curr = currency_db.find((data) => String(data.id) === String(value));
    setSelected_currency_id(curr.id);
    setCurrencyRate(curr.currency_rate);
  };

  const handleAmountBlur = (e) => {
    // Strip existing commas to prevent NaN errors when parsing (e.g., "1,500.5")
    const rawValue = e.target.value.replace(/,/g, "");

    if (rawValue && !isNaN(rawValue)) {
      // Force exactly two decimal places
      const formattedValue = parseFloat(rawValue).toFixed(2);

      // Pass the formatted value back to the hook using a synthetic event
      handleTotalAmountChange({ target: { value: formattedValue } });
    }
  };

  // Manually changing THIS card's Expenses Date detaches it from the shared
  // date going forward, and keeps the same "Due Date defaults to 15 days
  // later" behavior, scoped to this card only.
  const handleCardExpensesDateChange = (date) => {
    setDateOverridden(true);
    onDateOverrideChange(entryKey, true);
    setExpenses_date(date);
    if (!date) return;
    setDueDate(addDays(new Date(date), 15));
    warnCutoffDate(date, "Expenses Date");
  };

  useImperativeHandle(ref, () => ({
    getPayload: () => {
      const formatTotalAmount = totalAmountDisplay.replace(/,/g, "");

      return {
        totalAmount:
          parseFloat(formatTotalAmount) || (hasFixedAmount ? prefill.totalFixedAssetAmount : ""), // prettier-ignore
        desc,
        expenses_date,
        dueDate,
        expensesSubType_id: expensesSubType_id?.value,
        transaction_id,
        foreign: generalForeign,
        selected_currency_id: hasFixedCurrency
          ? prefill.currencyId
          : selected_currency_id,
        productName: null,
        unitPrice: null,
        assetQuantity: null,
        accountable: !isExternalAccountable
          ? (accountable?.value ?? null)
          : null,
        currencyRate: currencyRate === "" ? null : currencyRate,
        fixedAssetIds: prefill?.fixedAssetIds || [],
        expensesCheque, // para sa yilujia kasi un ang pag babasihan nila
        isExternalAccountable,
        externalAccountableText: isExternalAccountable
          ? externalAccountableText
          : null,
      };
    },
  }));

  const currencySelectValue = hasFixedCurrency
    ? prefill.currencyId
    : selected_currency_id;
  const currencyLocked = hasFixedCurrency && hasFixedAmount;

  // Symbol in front of the Amount input follows the selected currency.
  const selectedCurrencyName = currency_db.find(
    (c) => String(c.id) === String(currencySelectValue),
  )?.currency_name;
  const currencySymbol = getCurrencySymbol(selectedCurrencyName);

  const handleCopyTransactionId = async () => {
    if (!clientCodeDisplay) return;
    try {
      await navigator.clipboard.writeText(clientCodeDisplay);
    } catch {
      // navigator.clipboard only exists on https / localhost - fall back to the
      // old execCommand route so the button still works on a plain-http host.
      const ta = document.createElement("textarea");
      ta.value = clientCodeDisplay;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setIdCopied(true);
  };

  return (
    <div className="expense-entry-card h-100 d-flex flex-column">
      <div className="ee-header">
        <span className="ee-badge" aria-hidden="true">
          <i className="fa-regular fa-file-lines"></i>
        </span>
        <div className="ee-heading">
          <div className="ee-title">Expense #{index + 1}</div>
          <div className="ee-subtitle">Record and manage expense details</div>
        </div>
        {canRemove && (
          <button type="button" className="ee-remove-btn" onClick={onRemove}>
            <i className="fa-solid fa-trash"></i>
            Remove
          </button>
        )}
      </div>

      <div className="ee-body">
        {/* Transaction ID: label + hint on the left, input + copy button on
            the right. Wraps to label-above-input when the card is narrow. */}
        <div className="ee-field-inline">
          <div className="ee-inline-label">
            <label
              htmlFor={`client-transaction-id-${entryKey}`}
              className="ee-label"
            >
              Transaction ID
            </label>
            <div className="ee-hint">Final number is assigned on save.</div>
          </div>
          <div className="ee-icon-field ee-inline-control">
            <input
              type="text"
              readOnly
              value={clientCodeDisplay}
              placeholder="Generating..."
              className="form-control form-control-sm ee-id-input"
              id={`client-transaction-id-${entryKey}`}
            />
            <button
              type="button"
              className="ee-copy-btn"
              onClick={handleCopyTransactionId}
              title={idCopied ? "Copied!" : "Copy transaction ID"}
              aria-label="Copy transaction ID"
            >
              <i
                className={
                  idCopied ? "fa-solid fa-check" : "fa-regular fa-copy"
                }
              ></i>
            </button>
          </div>
        </div>

        <div className="ee-field">
          <div className="ee-row-between">
            <label htmlFor={`accountable-${entryKey}`} className="ee-label">
              Accountable
            </label>
            <div
              className="ee-radio-group"
              role="radiogroup"
              aria-label="Accountable type"
            >
              <label className="ee-radio" title="Internal Accountable Person">
                <input
                  type="radio"
                  className="ee-radio-input"
                  name={`accountableType-${entryKey}`}
                  checked={!isExternalAccountable}
                  onChange={() => setIsExternalAccountable(false)}
                />
                Internal
              </label>
              <label className="ee-radio" title="External Accountable Person">
                <input
                  type="radio"
                  className="ee-radio-input"
                  name={`accountableType-${entryKey}`}
                  checked={isExternalAccountable}
                  onChange={() => setIsExternalAccountable(true)}
                />
                External
              </label>
            </div>
          </div>
          {isExternalAccountable ? (
            <div className="ee-icon-field">
              <input
                type="text"
                required
                id={`accountable-${entryKey}`}
                value={externalAccountableText}
                onChange={(e) => setExternalAccountableText(e.target.value)}
                className="form-control form-control-sm ee-has-icon"
                placeholder="Enter external accountable person"
              />
              <i
                className="fa-regular fa-user ee-field-icon"
                aria-hidden="true"
              ></i>
            </div>
          ) : (
            <div className="ee-icon-field">
              <Select
                inputId={`accountable-${entryKey}`}
                options={userOptions}
                value={accountable}
                onChange={(selected) => setAccountable(selected)}
                placeholder="Select Accountable Person"
                styles={themeSelectStyles(
                  compactSelectStyles(
                    accountable ?? "optional",
                    validated,
                    "0.55rem",
                  ),
                )}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <i
                className="fa-regular fa-user ee-field-icon"
                aria-hidden="true"
              ></i>
            </div>
          )}
        </div>

        <div className="ee-field">
          <label htmlFor={`expenses-type-1-${entryKey}`} className="ee-label">
            Expenses Sub-Type (Expenses Main Type){" "}
            <span className="ee-required">*</span>
          </label>
          <div className="ee-icon-field">
            <Select
              inputId={`expenses-type-1-${entryKey}`}
              options={expenseType1Options}
              value={expensesSubType_id}
              onChange={handleExpensesChange}
              placeholder={`Select Expense Sub-Type`}
              styles={themeSelectStyles(
                compactSelectStyles(expensesSubType_id, validated, "0.55rem"),
              )}
              required
            />
            <i
              className="fa-regular fa-folder-open ee-field-icon"
              aria-hidden="true"
            ></i>
          </div>
        </div>

        {/* Due Date is not shown on the card - it still follows the general
            Due Date (or Expenses Date + 15 days once this card's Expenses
            Date is edited) and is sent in getPayload(). */}
        {/* Expenses Date + Cheque Number side by side; wraps to stacked
            when the card is too narrow (same grid the date pair used). */}
        <div className="ee-date-grid">
          <div className="ee-field">
            <label htmlFor={`expenses_date-${entryKey}`} className="ee-label">
              Expenses Date <span className="ee-required">*</span>
              {!dateOverridden && <span className="ee-synced">(synced)</span>}
            </label>
            <div className="ee-date-control">
              <CustomDatePicker
                label={"Expenses Date"}
                selected={expenses_date ? new Date(expenses_date) : ""}
                // Cutoff warning is inside handleCardExpensesDateChange
                // (replaces dateValidation from useDateValidation)
                handleDateChange={handleCardExpensesDateChange}
                setter={handleCardExpensesDateChange}
                CustomInput={CompactCustomInput}
                isRequired={true}
                validated={validated}
                iconTopOffset={"0.5rem"}
              />
            </div>
          </div>

          <div className="ee-field">
            <label htmlFor={`cheque-number-${entryKey}`} className="ee-label">
              Cheque Number
            </label>
            <div className="ee-icon-field ee-date-control">
              <input
                type="text"
                value={expensesCheque}
                onChange={(e) => setExpensesCheque(e.target.value)}
                placeholder="Optional"
                className="form-control form-control-sm ee-has-icon"
                id={`cheque-number-${entryKey}`}
              />
              <i
                className="fa-regular fa-credit-card ee-field-icon"
                aria-hidden="true"
              ></i>
            </div>
          </div>
        </div>

        <div className="ee-field">
          <label htmlFor={`amount-${entryKey}`} className="ee-label">
            Amount <span className="ee-required">*</span>
            {selected_currency_id &&
            selected_currency_id !== "11111111-1111-1111-1111-111111111111" ? (
              <span className="ee-rate">Current Rate: {currencyRate}</span>
            ) : null}
          </label>
          <div className="ee-amount-row">
            <select
              required
              aria-label="Currency"
              onChange={(e) => handleChangeCurrency(e.target.value)}
              className="form-select form-select-sm ee-currency-select"
              value={currencySelectValue}
              disabled={currencyLocked}
            >
              <option value="" disabled>
                Select Currency
              </option>
              {currency_db.map((data) => (
                <option key={data.id} value={data.id}>
                  {`${data.currency_name}`}
                </option>
              ))}
            </select>
            <div className="input-group input-group-sm ee-amount-group z-0">
              <span className="input-group-text">
                {currencySymbol ? (
                  currencySymbol
                ) : (
                  <i className="fa-solid fa-coins" aria-hidden="true"></i>
                )}
              </span>
              <input
                type="text"
                value={totalAmountDisplay}
                required
                placeholder="0.00"
                onChange={handleTotalAmountChange}
                onBlur={handleAmountBlur}
                className="form-control form-control-sm z-0"
                id={`amount-${entryKey}`}
                readOnly={currencyLocked}
                maxLength={decimal ? 20 : 19}
              />
            </div>
          </div>
        </div>

        <div className="ee-field">
          <label htmlFor={`remarks-${entryKey}`} className="ee-label">
            Description
          </label>
          <div className="ee-icon-field">
            <Form.Control
              id={`remarks-${entryKey}`}
              as="textarea"
              size="sm"
              rows={2}
              className="ee-has-icon ee-textarea"
              placeholder="Enter description"
              maxLength={DESC_MAX_LENGTH}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
            <i
              className="fa-regular fa-file-lines ee-field-icon ee-field-icon-top"
              aria-hidden="true"
            ></i>
            <span className="ee-counter">
              {desc.length} / {DESC_MAX_LENGTH}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

const NewAddedstyleExpenses = () => {
  const { currencyId, totalFixedAssetAmount } = useParams();
  const { state } = useLocation();
  const ids = state?.ids || [];
  const userLoggedID = useDecodeToken();

  const navigate = useNavigate();

  const [validated, setValidated] = useState(false);
  const [expensesType_db, setExpensesType_db] = useState([]);
  const [currency_db, setCurrency_db] = useState([]);
  const [userList, setUserList] = useState([]);

  // Keys for each rendered card. Starts with a single entry.
  const [entryKeys, setEntryKeys] = useState([0]);
  const nextKeyRef = useRef(1);
  const entryRefs = useRef({});

  // Optional pagination, off by default (see the "Paginate cards" checkbox
  // in the General Fields box header). Same idea as NewAddExpenses.jsx's
  // always-on pagination, just gated behind a toggle and capped at 7 per
  // page instead of 2.
  const EXPENSE_ENTRY_PAGE_SIZE = 6;
  const [paginationEnabled, setPaginationEnabled] = useState(false);
  const [entryPage, setEntryPage] = useState(0);

  // Shared "general" Expenses Date / Due Date, shown once above the card
  // grid. Every card follows these values until its own date fields are
  // edited directly (see dateOverridden inside ExpenseEntryFields) -
  // changing these updates every not-yet-overridden card at once; a card
  // edited internally keeps its own value regardless of what happens here.
  const [generalExpensesDate, setGeneralExpensesDate] = useState(dateToday());
  const [generalDueDate, setGeneralDueDate] = useState(() =>
    addDays(new Date(dateToday()), 15),
  );
  const [generalForeign, setGeneralForeign] = useState("Local");

  // General Due Date defaults to 15 days after General Expenses Date,
  // same pattern the per-entry fields used to follow on their own.
  useEffect(() => {
    if (!generalExpensesDate) return;
    setGeneralDueDate(addDays(new Date(generalExpensesDate), 15));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalExpensesDate]);

  // Keys of the cards currently detached from the general Expenses Date /
  // Due Date (dateOverridden === true on that card). Drives whether
  // changing the general date needs to ask what to do about them.
  const [overriddenKeys, setOverriddenKeys] = useState(() => new Set());
  const handleEntryOverrideChange = useCallback((key, isOverridden) => {
    setOverriddenKeys((prev) => {
      if (prev.has(key) === isOverridden) return prev;
      const next = new Set(prev);
      if (isOverridden) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  // Bumped to force every card - including detached ones - back in sync
  // with the general Expenses Date / Due Date. 0 means "never triggered".
  const [forceSyncToken, setForceSyncToken] = useState(0);

  // Asks whether a general date change should also override cards that
  // already have their own custom date, or only continue updating the
  // cards still following the general date.
  const confirmGeneralDateChange = (fieldLabel) =>
    swal({
      title: `Update ${fieldLabel}?`,
      text: "One or more expense cards already have their own custom date. Apply this change to every card, or only to the cards still following the general date?",
      icon: "warning",
      className: "date-confirm-swal",
      buttons: {
        cancel: "Cancel",
        unchanged: {
          text: "Only Unchanged Cards",
          value: "unchanged",
          className: "swal-button--unchanged",
        },
        all: {
          text: "Change All Cards",
          value: "all",
          className: "swal-button--all",
        },
      },
    });

  const handleGeneralExpensesDateChange = (date) => {
    if (!date || overriddenKeys.size === 0) {
      setGeneralExpensesDate(date);
      warnCutoffDate(date, "Expenses Date");
      return;
    }

    confirmGeneralDateChange("Expenses Date").then((choice) => {
      if (!choice) return; // cancelled - leave every date as it was

      // Warn only after the confirm dialog closes (one swal at a time)
      warnCutoffDate(date, "Expenses Date");

      if (choice === "all") {
        // Set both explicitly, in the same batch as the token bump below,
        // so every card's force-sync effect reads the already-cascaded
        // Due Date instead of a stale one (the auto-cascade effect further
        // up would otherwise land it a render late).
        setGeneralExpensesDate(date);
        setGeneralDueDate(addDays(new Date(date), 15));
        setOverriddenKeys(new Set());
        setForceSyncToken((t) => t + 1);
      } else {
        setGeneralExpensesDate(date);
      }
    });
  };

  const handleGeneralDueDateChange = (date) => {
    if (!date || overriddenKeys.size === 0) {
      setGeneralDueDate(date);
      warnCutoffDate(date, "Due Date");
      return;
    }

    confirmGeneralDateChange("Due Date").then((choice) => {
      if (!choice) return;

      warnCutoffDate(date, "Due Date");

      if (choice === "all") {
        setGeneralDueDate(date);
        setOverriddenKeys(new Set());
        setForceSyncToken((t) => t + 1);
      } else {
        setGeneralDueDate(date);
      }
    });
  };

  const fetchNewCode = useCallback(async () => {
    try {
      const res = await axios.get(BASE_URL + "/expenses/getCode");
      return res.data;
    } catch (err) {
      console.log(err);
      return "";
    }
  }, []);

  // Next display code for this month, e.g. { prefix: "EXP-26-10-", nextSeq: 5 }.
  // Card #1 shows nextSeq, card #2 nextSeq + 1, ... The server assigns the
  // real code on save, so this is only a preview.
  const [clientCodePreview, setClientCodePreview] = useState(null);
  const fetchClientCodePreview = async () => {
    await axios
      .get(BASE_URL + "/expenses/getClientCodePreview")
      .then((response) => {
        setClientCodePreview(response.data);
      })
      .catch((error) => {
        console.error("Error fetching client code preview:", error);
      });
  };

  const getClientCodeDisplay = (index) =>
    clientCodePreview
      ? `${clientCodePreview.prefix}${String(
          clientCodePreview.nextSeq + index,
        ).padStart(4, "0")}`
      : "";

  const fetchUsers = async () => {
    await axios
      .get(BASE_URL + "/masterList/fetchAllUsers")
      .then((response) => {
        setUserList(response.data);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
  };

  const fetchExpenses = async () => {
    await axios
      .get(BASE_URL + "/expenses2/fetchTable")
      .then((response) => {
        setExpensesType_db(response.data);
      })
      .catch((error) => {
        console.error("Error fetching roles:", error);
      });
  };

  const fetchCurrency = async () => {
    await axios
      .get(BASE_URL + "/currency/fetchCurrency")
      .then((response) => {
        setCurrency_db(response.data);
      })
      .catch((error) => {
        console.error("Error fetching roles:", error);
      });
  };

  useEffect(() => {
    fetchClientCodePreview();
    fetchExpenses();
    fetchCurrency();
    fetchUsers();
  }, []);

  // User options for ACCOUNTABLE dropdown
  const userOptions = userList.map((user) => ({
    value: user.id,
    label: `${user.fname} ${user.mname ? user.mname + " " : ""}${user.lname}`,
  }));

  // Expense Type 1 Options for dropdown select
  const expenseType1Options = expensesType_db.map((item) => ({
    value: item.id,
    label: `${item.sub_type} - (${item.expenses_one.expenses_type_one})`,
  }));

  const handleAddEntry = () => {
    const newKey = nextKeyRef.current++;
    const updatedKeys = [...entryKeys, newKey];
    setEntryKeys(updatedKeys);
    // Jump to the page that contains the newly added entry.
    if (paginationEnabled) {
      setEntryPage(
        Math.floor((updatedKeys.length - 1) / EXPENSE_ENTRY_PAGE_SIZE),
      );
    }
  };

  const handleRemoveEntry = (key) => {
    const updatedKeys = entryKeys.filter((k) => k !== key);
    setEntryKeys(updatedKeys);
    delete entryRefs.current[key];
    setOverriddenKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

    // Step back a page if the current page no longer has any entries.
    if (paginationEnabled) {
      const newTotalPages = Math.ceil(
        updatedKeys.length / EXPENSE_ENTRY_PAGE_SIZE,
      );
      if (entryPage >= newTotalPages && entryPage > 0) {
        setEntryPage((p) => p - 1);
      }
    }
  };

  const add = async (e) => {
    // Save button
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();

      // A required field on a page that isn't showing right now is still
      // "invalid" as far as the browser is concerned, it's just invisible.
      // If paginated, jump to whichever page has it before scrolling.
      const firstInvalid = form.querySelector(":invalid");
      const invalidEntry = firstInvalid?.closest("[data-entry-index]");
      if (invalidEntry) {
        if (paginationEnabled) {
          const invalidIndex = parseInt(invalidEntry.dataset.entryIndex, 10);
          setEntryPage(Math.floor(invalidIndex / EXPENSE_ENTRY_PAGE_SIZE));
          // Wait for the page switch to re-render (the card is display:none
          // until then) before scrolling to it.
          setTimeout(() => {
            invalidEntry.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }, 0);
        } else {
          invalidEntry.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }

      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
      setValidated(true);
      return;
    }

    // Expenses Date must not be later than Due Date, checked per entry.
    // Mirrors the required-field check above: jump to whichever page holds
    // the offending entry (if paginated), then scroll it into view.
    for (let index = 0; index < entryKeys.length; index++) {
      const payload = entryRefs.current[entryKeys[index]]?.getPayload();
      if (!payload) continue;

      const { expenses_date, dueDate } = payload;
      if (
        expenses_date &&
        dueDate &&
        toDateOnly(expenses_date) > toDateOnly(dueDate)
      ) {
        const entryEl = document.querySelector(`[data-entry-index="${index}"]`);
        if (paginationEnabled) {
          setEntryPage(Math.floor(index / EXPENSE_ENTRY_PAGE_SIZE));
          setTimeout(() => {
            entryEl?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 0);
        } else {
          entryEl?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        swal({
          icon: "error",
          title: "Invalid Date",
          text: `Expense #${index + 1}: Expenses Date cannot be later than Due Date.`,
        });
        setValidated(true);
        return;
      }
    }

    const isMultiple = entryKeys.length > 1;

    swal({
      title: `Create ${isMultiple ? "these" : "this"} new expense${
        isMultiple ? "s" : ""
      }?`,
      text: "",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;

      try {
        for (const key of entryKeys) {
          const payload = entryRefs.current[key]?.getPayload();
          if (!payload) continue;

          const res = await axios.post(`${BASE_URL}/expenses/createExpenses`, {
            ...payload,
            userLoggedID,
          });

          if (res.status === 201) {
            swal({
              title: "Opppss!",
              text: "Cutoff already posted for this date",
              icon: "warning",
              buttons: false,
              timer: 2000,
              dangerMode: true,
            });
            return;
          } else if (res.status !== 200) {
            swal({
              title: "Something Went Wrong",
              text: "Please contact your support immediately",
              icon: "error",
              buttons: false,
              timer: 2000,
              dangerMode: true,
            });
            return;
          }
        }

        swal({
          title: "Success",
          text: `Expense${isMultiple ? "s" : ""} created successfully`,
          icon: "success",
          buttons: false,
          timer: 2000,
          dangerMode: true,
        }).then(() => {
          navigate("/accounting/expenses");
        });
      } catch (err) {
        if (err.response && err.response.status === 409) {
          swal({
            icon: "error",
            title: "Transaction ID Conflict",
            text: "The transaction ID you entered already exists in the system. Please use a unique identifier.",
          });
          return;
        }

        swal({
          title: "Something Went Wrong",
          text: "Please contact your support immediately",
          icon: "error",
          buttons: false,
          timer: 2000,
          dangerMode: true,
        });
      }
    });

    setValidated(true);
  };

  // .general-fields-box below and navbar.jsx's <nav> (.custom-navbar,
  // Bootstrap sticky-top) both currently stick at top: 0 in the same
  // scroll context, so once you scroll, the navbar (higher z-index)
  // paints over this box. Measure the navbar's real rendered height and
  // feed it in as --navbar-h instead of guessing a px value, so the box
  // sticks directly under the navbar and stays correct if navbar height
  // ever changes (e.g. its holiday-season banner). navbar.jsx itself is
  // untouched - this only reads its rendered height.
  const generalFieldsBoxRef = useRef(null);
  useEffect(() => {
    const navEl = document.querySelector(".custom-navbar");
    const boxEl = generalFieldsBoxRef.current;
    if (!navEl || !boxEl) return;

    const setNavOffset = () => {
      boxEl.style.setProperty("--navbar-h", `${navEl.offsetHeight}px`);
    };

    setNavOffset();
    const ro = new ResizeObserver(setNavOffset);
    ro.observe(navEl);
    return () => ro.disconnect();
  }, []);

  // Clamp against entryKeys shrinking (e.g. removing entries down to fewer
  // than the page the user was previously on) instead of stashing a
  // separately-corrected page in state.
  const totalPages = Math.ceil(entryKeys.length / EXPENSE_ENTRY_PAGE_SIZE) || 1;
  const currentPage = Math.min(entryPage, totalPages - 1);
  const pageStart = currentPage * EXPENSE_ENTRY_PAGE_SIZE;

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3">
            <Link to="/accounting/expenses" className="text-dark me-2">
              <i className="fa-solid fa-arrow-left"></i>
            </Link>
            ADD EXPENSES
          </span>
        </div>
      </div>

      <Form noValidate validated={validated} onSubmit={add}>
        <div
          ref={generalFieldsBoxRef}
          className="border rounded-3 bg-light p-3 mb-4 mx-2 general-fields-box"
        >
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
            <div className="fw-bold">
              General Fields{" "}
              <span className="text-muted fw-normal small">
                (applies to every card below; if a card's own Expenses Date or
                Due Date has been changed, you'll be asked whether to update it
                too)
              </span>
            </div>
            <Form.Check
              type="checkbox"
              id="paginate-cards-toggle"
              label={`Paginate cards (${EXPENSE_ENTRY_PAGE_SIZE} per page)`}
              checked={paginationEnabled}
              onChange={(e) => setPaginationEnabled(e.target.checked)}
              className="flex-shrink-0"
            />
          </div>
          <div className="general-fields-row-wrap">
            <div className="general-fields-row">
              <div className="general-fields-foreign">
                <label className="fw-bold d-block mb-1">Foreign Type :</label>
                <div className="general-fields-foreign-inner">
                  <Form.Check
                    type="radio"
                    name="generalForeignType"
                    id="general-Local"
                    label="Local"
                    value="Local"
                    checked={generalForeign === "Local"}
                    onClick={() => setGeneralForeign("Local")}
                    className="me-3"
                  />
                  <Form.Check
                    type="radio"
                    name="generalForeignType"
                    id="general-Overseas"
                    label="Overseas"
                    value="Overseas"
                    checked={generalForeign === "Overseas"}
                    onClick={() => setGeneralForeign("Overseas")}
                  />
                </div>
              </div>
              <div className="general-fields-date">
                <label className="d-block">
                  Expenses Date <span className="text-danger">*</span>
                </label>
                <CustomDatePicker
                  label={"Expenses Date"}
                  selected={
                    generalExpensesDate ? new Date(generalExpensesDate) : ""
                  }
                  // Cutoff warning is inside handleGeneralExpensesDateChange
                  // (replaces dateValidation from useDateValidation)
                  handleDateChange={handleGeneralExpensesDateChange}
                  setter={handleGeneralExpensesDateChange}
                  CustomInput={CustomInput}
                  isRequired={true}
                  validated={validated}
                  iconTopOffset={"0.8rem"}
                  maxDate={
                    generalDueDate ? new Date(generalDueDate) : undefined
                  }
                />
              </div>
              <div className="general-fields-date">
                <label className="d-block">
                  Due Date <span className="text-danger">*</span>
                </label>
                <CustomDatePicker
                  label={"Due Date"}
                  selected={generalDueDate ? new Date(generalDueDate) : ""}
                  // Cutoff warning is inside handleGeneralDueDateChange
                  // (replaces dateValidation from useDateValidation)
                  handleDateChange={handleGeneralDueDateChange}
                  setter={handleGeneralDueDateChange}
                  CustomInput={CustomInput}
                  isRequired={true}
                  validated={validated}
                  iconTopOffset={"0.8rem"}
                  minDate={
                    generalExpensesDate
                      ? new Date(generalExpensesDate)
                      : undefined
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 mx-1 mb-3 justify-content-center">
          {entryKeys.map((key, index) => {
            // When paginated, entries off the current page stay mounted (so
            // their data isn't lost) but are visually hidden instead of
            // unmounted - same approach NewAddExpenses.jsx uses.
            const isHidden =
              paginationEnabled &&
              (index < pageStart ||
                index >= pageStart + EXPENSE_ENTRY_PAGE_SIZE);

            return (
              <div
                key={key}
                data-entry-index={index}
                className="col"
                style={isHidden ? { display: "none" } : undefined}
              >
                <ExpenseEntryFields
                  entryKey={key}
                  index={index}
                  canRemove={entryKeys.length > 1}
                  onRemove={() => handleRemoveEntry(key)}
                  currency_db={currency_db}
                  expensesType_db={expensesType_db}
                  userOptions={userOptions}
                  expenseType1Options={expenseType1Options}
                  validated={validated}
                  fetchNewCode={fetchNewCode}
                  clientCodeDisplay={getClientCodeDisplay(index)}
                  generalExpensesDate={generalExpensesDate}
                  generalDueDate={generalDueDate}
                  generalForeign={generalForeign}
                  onDateOverrideChange={handleEntryOverrideChange}
                  forceSyncToken={forceSyncToken}
                  prefill={
                    index === 0
                      ? {
                          currencyId,
                          totalFixedAssetAmount,
                          fixedAssetIds: ids,
                        }
                      : null
                  }
                  ref={(el) => {
                    if (el) entryRefs.current[key] = el;
                  }}
                />
              </div>
            );
          })}
        </div>

        {paginationEnabled && totalPages > 1 && (
          <div className="w-100 d-flex justify-content-between align-items-center flex-wrap gap-2 mx-1 mb-4 px-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={currentPage === 0}
              onClick={() => setEntryPage(currentPage - 1)}
            >
              ‹ Prev
            </button>

            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">Page</small>
              <select
                className="form-select form-select-sm w-auto"
                value={currentPage}
                onChange={(e) => setEntryPage(Number(e.target.value))}
                aria-label="Jump to page"
              >
                {Array.from({ length: totalPages }, (_, i) => (
                  <option key={i} value={i}>
                    {i + 1}
                  </option>
                ))}
              </select>
              <small className="text-muted">of {totalPages}</small>
            </div>

            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setEntryPage(currentPage + 1)}
            >
              Next ›
            </button>
          </div>
        )}

        <div className="sticky-action-bar">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mx-1">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleAddEntry}
            >
              <i className="fa-solid fa-plus me-2"></i>
              Add Expenses
            </button>

            <div className="d-flex flex-row gap-2">
              <button
                className="btn ee-btn-cancel"
                type="button"
                onClick={() => navigate("/accounting/expenses")}
              >
                Cancel
              </button>
              <button type="submit" className="btn ee-btn-save">
                <i className="fa-regular fa-floppy-disk"></i>
                Save Expense
              </button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default NewAddedstyleExpenses;
